import {
  BankDebitMethod,
  Country,
  Currency,
  DefaultLanguage,
  EmailMarketingOption,
  EnableSendRecurringChargeEmailsOption,
  GateGeoCheckInByDistance,
  IsRemainingSpotCountPublicOption,
  Language,
  TaxPricingOption,
} from '../types/location';
import { safeFeeFloat } from './number';

const validateBoolean = (value: string) => {
  if (!value) return null;

  const lowerValue = value.toLowerCase();
  if (lowerValue === 'true') return true;
  if (lowerValue === 'false') return false;

  return value;
};

const mappers = {
  boolean: (value: string) => validateBoolean(value),
  optionalBoolean: (value: string) => validateBoolean(value),
  integer: (value: string) => (value ? parseInt(value) : null),
  float: (value: string) => (value ? parseFloat(value) : null),
  string: (value: string) => value ?? '',
};

export const mapCsvToLocation: Record<
  string,
  { key: string; mapper: (value: string) => unknown }
> = {
  'Location Name': {
    key: 'Name',
    // Remove extra spaces and special characters
    mapper: (value) =>
      (value ?? '')
        .replace(/[^\w\s]/gi, '')
        .replace(/\s+/g, ' ')
        .trim(),
  },
  'Mariana Tek Region': {
    key: 'RegionName',
    mapper: mappers.string,
  },
  'Primary Language': {
    key: 'PrimaryLanguage',
    mapper: (value: string) => {
      if (value) {
        const keys = Object.keys(Language) as (keyof typeof Language)[];
        const key = keys.find((k) => k === value);
        return key ? Language[key] : value;
      }
      return null;
    },
  },
  'Default Language': {
    key: 'DefaultLanguage',
    mapper: (value: string) => {
      if (value) {
        const keys = Object.keys(
          DefaultLanguage,
        ) as (keyof typeof DefaultLanguage)[];
        const key = keys.find((k) => k === value);
        return key ? DefaultLanguage[key] : value;
      }
      return null;
    },
  },
  Country: {
    key: 'Country',
    mapper: (value: string) => {
      if (value) {
        const keys = Object.keys(Country) as (keyof typeof Country)[];
        const key = keys.find((k) => k === value);
        return key ? Country[key] : value;
      }
      return null;
    },
  },
  'Street Address 1': {
    key: 'AddressLine1',
    mapper: mappers.string,
  },
  'Street Address 2 (eg Apt Num)': {
    key: 'AddressLine2',
    mapper: mappers.string,
  },
  'Street Address 3': {
    key: 'AddressLine3',
    mapper: mappers.string,
  },
  City: {
    key: 'AddressCity',
    mapper: mappers.string,
  },
  'State/Province': {
    key: 'StateProvince',
    mapper: mappers.string,
  },
  'Post/Zip-code': {
    key: 'PostalCode',
    mapper: mappers.string,
  },
  'Location Phone Number': {
    key: 'PhoneNumber',
    mapper: (value: string) => {
      if (!value) return '';
      return value.startsWith('+') ? value : `+${value}`;
    },
  },
  'Location Email': {
    key: 'EmailAddress',
    mapper: mappers.string,
  },
  'Stripe Charge Back Email': {
    key: 'ChargeBackEmail',
    mapper: mappers.string,
  },
  'Currency Code': {
    key: 'CurrencyCode',
    mapper: (value: string) => {
      if (value) {
        const keys = Object.keys(Currency) as (keyof typeof Currency)[];
        const key = keys.find((k) => k === value);
        return key ? Currency[key] : value;
      }
      return null;
    },
  },
  'Display Waitlist Position': {
    key: 'IsWaitlistPositionPublic',
    mapper: mappers.boolean,
  },
  'Display Waitlist Count': {
    key: 'IsWaitlistCountPublic',
    mapper: mappers.boolean,
  },
  'Display Remaining Spot Count': {
    key: 'IsRemainingSpotCountPublic',
    mapper: (value: string) => {
      if (value) {
        const keys = Object.keys(
          IsRemainingSpotCountPublicOption,
        ) as (keyof typeof IsRemainingSpotCountPublicOption)[];
        const key = keys.find((k) => k === value);
        return key ? IsRemainingSpotCountPublicOption[key] : value;
      }
      return null;
    },
  },
  'Enable Spot Changes': {
    key: 'EnableChangeSpots',
    mapper: mappers.boolean,
  },
  'ClassPass Enabled': {
    key: 'EnableClasspass',
    mapper: mappers.optionalBoolean,
  },
  'Enable Third Party Spot Changes': {
    key: 'EnableThirdPartyChangeSpots',
    mapper: mappers.boolean,
  },
  'Daily Pending Reservation Limit': {
    key: 'DailyReservationLimit',
    mapper: mappers.integer,
  },
  'Enable Mobile Check-in': {
    key: 'EnableGeoCheckIn',
    mapper: mappers.boolean,
  },
  'Gate Geo Check in By Distance': {
    key: 'GateGeoCheckInByDistance',
    mapper: (value: string) => {
      if (value) {
        const keys = Object.keys(
          GateGeoCheckInByDistance,
        ) as (keyof typeof GateGeoCheckInByDistance)[];
        const key = keys.find((k) => k === value);
        return key ? GateGeoCheckInByDistance[key] : value;
      }
      return null;
    },
  },
  'Geo Check In Distance in meters (if Applicable)': {
    key: 'GeoCheckInDistance',
    mapper: mappers.float,
  },
  'Enable Royalty Fee Platform': {
    key: 'EnableRoyaltyFeePlatform',
    mapper: mappers.boolean,
  },
  'Primary Classroom': {
    key: 'PrimaryClassroom',
    mapper: mappers.string,
  },
  'Enable Prechecked Email Marketing': {
    key: 'EnablePreCheckedNewsletterSubscription',
    mapper: (value: string) => {
      if (value) {
        const keys = Object.keys(
          EmailMarketingOption,
        ) as (keyof typeof EmailMarketingOption)[];
        const key = keys.find((k) => k === value);
        return key ? EmailMarketingOption[key] : value;
      }
      return null;
    },
  },
  'Enable Recurring Charge Emails': {
    key: 'EnableSendRecurringChargeEmails',
    mapper: (value: string) => {
      if (value) {
        const keys = Object.keys(
          EnableSendRecurringChargeEmailsOption,
        ) as (keyof typeof EnableSendRecurringChargeEmailsOption)[];
        const key = keys.find((k) => k === value);
        return key ? EnableSendRecurringChargeEmailsOption[key] : value;
      }
      return null;
    },
  },
  'Late Cancel Fee': {
    key: 'LateCancelFee',
    mapper: safeFeeFloat,
  },
  'No Show Fee': {
    key: 'NoShowFee',
    mapper: safeFeeFloat,
  },
  'Use Tax Inclusive Pricing': {
    key: 'UseTaxInclusivePricing',
    mapper: (value: string) => {
      if (value) {
        const keys = Object.keys(
          TaxPricingOption,
        ) as (keyof typeof TaxPricingOption)[];
        const key = keys.find((k) => k === value);
        return key ? TaxPricingOption[key] : value;
      }
      return undefined;
    },
  },
  'Business Number (if applicable)': {
    key: 'BusinessNumber',
    mapper: mappers.string,
  },
  'Stripe Account ID': {
    key: 'StripeAccountId',
    mapper: mappers.string,
  },
  'Stripe Statement Descriptor Text': {
    key: 'StripeStatementDescriptor',
    // Remove extra spaces and special characters
    mapper: (value) =>
      (value ?? '').replace(/[^\w\s]/gi, '').replace(/\s+/g, ' '),
  },
  'Percentage Fee for Non- Terminal Charges': {
    key: 'PercentageFee',
    mapper: mappers.float,
  },
  'Flat Fee for Non-Terminal Charges': {
    key: 'FlatFee',
    mapper: mappers.float,
  },
  'Percentage Fee for Terminal Charges': {
    key: 'TerminalPercentageFee',
    mapper: mappers.float,
  },
  'Flat Fee for Terminal Charges': {
    key: 'TerminalFlatFee',
    mapper: mappers.float,
  },
  bank_debit: {
    key: 'BankDebitMethod',
    mapper: (value: string) => {
      const normalizedValue = value?.trim().toLowerCase();
      if (!normalizedValue) return undefined;

      return Object.values(BankDebitMethod).includes(
        normalizedValue as BankDebitMethod,
      )
        ? (normalizedValue as BankDebitMethod)
        : normalizedValue;
    },
  },
  'Percentage Fee for Direct Debit Charges': {
    key: 'DirectDebitPercentageFee',
    mapper: (value: string) => (value?.trim() ? Number(value) : undefined),
  },
  'Flat Fee for Direct Debit Charges': {
    key: 'DirectDebitFlatFee',
    mapper: (value: string) => (value?.trim() ? Number(value) : undefined),
  },
};
